/**
 * PremiumPackTeaser — friendly intermediate sheet shown when a free user
 * taps a locked Style Pack in the iteration strip.
 *
 * Why this component exists:
 *   The PackIterationStrip used to surface the FULL 3-tier PremiumModal on
 *   any locked-pack tap. That's a hard jump — user tapped a pack, suddenly
 *   stares at a pricing page. This teaser is the soft-sell in between:
 *
 *     • Names the specific pack they tapped (recognition before pitch)
 *     • Shows the pack's signature swatch (visual proof of what they unlock)
 *     • 3 crisp value props tied to Premium (Style Packs, Motion, Creator Studio)
 *     • Two CTAs: "see all premium perks" (opens the existing PremiumModal)
 *                 "maybe later" (dismiss)
 *
 * Source: iter-72 "(b) what Premium gets you" — addresses the silent-padlock
 *   complaint surfaced in the monetization audit.
 */
import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Sparkles, Wand2, Palette, X } from "lucide-react";
import { STYLE_PACKS } from "../lib/stylePacks";
import { track } from "../lib/track";

const PREMIUM_VALUE_BULLETS = [
    {
        icon: Palette,
        title: "All 11 Style Packs",
        sub: "vintage · brutalist · polaroid · watercolor · playful · luxury",
    },
    {
        icon: Wand2,
        title: "Motion treatments",
        sub: "drift · breathe · shimmer · glow — cards that move when shared",
    },
    {
        icon: Sparkles,
        title: "Creator Studio + clean exports",
        sub: "signature line · brand colors · logo · watermark-free PNGs",
    },
];

export default function PremiumPackTeaser({ open, packId, onClose, onSeeAll }) {
    const pack = packId && STYLE_PACKS[packId];
    const preview = pack?.variants?.[0];

    useEffect(() => {
        if (open && packId) {
            track("premium_pack_teaser_open", { pack_id: packId });
        }
    }, [open, packId]);

    if (!pack) return null;

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={onClose}
                        className="fixed inset-0 z-50 bg-black/65 backdrop-blur-sm"
                        data-testid="premium-pack-teaser-backdrop"
                    />
                    <motion.div
                        initial={{ y: 60, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 60, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed left-0 right-0 bottom-0 z-50 px-3 pb-5 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:px-4 sm:pb-0 pointer-events-none"
                        data-testid="premium-pack-teaser"
                    >
                        <div className="pointer-events-auto mx-auto w-full max-w-md rounded-3xl bg-[#161618] border border-white/10 shadow-2xl overflow-hidden">
                            {/* Pack preview header — visual proof of what they're unlocking */}
                            <div
                                className="relative h-32 sm:h-40 w-full flex items-end justify-between p-4"
                                style={{
                                    background: preview?.background || "#222",
                                }}
                            >
                                <div className="flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase font-medium px-2.5 py-1 rounded-full bg-black/45 text-white/90 backdrop-blur-sm">
                                    <Lock className="w-3 h-3" />
                                    <span>Premium pack</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="w-8 h-8 rounded-full bg-black/45 text-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-black/65 transition-colors"
                                    aria-label="Close"
                                    data-testid="premium-pack-teaser-close"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Body — name + value props */}
                            <div className="p-5 sm:p-6">
                                <div className="text-[11px] tracking-[0.22em] uppercase text-amber-300/80 mb-1">
                                    you tapped
                                </div>
                                <h3 className="text-xl sm:text-2xl font-display text-white">
                                    {pack.label}
                                </h3>
                                {pack.description && (
                                    <p className="mt-1.5 text-sm text-white/55 leading-relaxed">
                                        {pack.description}
                                    </p>
                                )}

                                <div className="mt-5 h-px w-full bg-white/8" />

                                <div className="mt-4 text-[11px] tracking-[0.22em] uppercase text-white/45 mb-3">
                                    what premium unlocks
                                </div>

                                <ul className="space-y-3">
                                    {PREMIUM_VALUE_BULLETS.map(({ icon: Icon, title, sub }, idx) => (
                                        <li
                                            key={idx}
                                            className="flex items-start gap-3"
                                            data-testid={`premium-pack-teaser-bullet-${idx}`}
                                        >
                                            <div className="w-8 h-8 shrink-0 rounded-full bg-amber-500/10 border border-amber-400/20 flex items-center justify-center mt-0.5">
                                                <Icon className="w-4 h-4 text-amber-300" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-sm text-white/90 font-medium">
                                                    {title}
                                                </div>
                                                <div className="text-xs text-white/45 leading-snug mt-0.5">
                                                    {sub}
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>

                                {/* CTAs */}
                                <div className="mt-6 flex flex-col gap-2">
                                    {/* iter-103 — price shown PROMINENTLY before the
                                        upgrade tap so users know exactly what they're
                                        committing to before sign-in. */}
                                    <div className="rounded-2xl bg-amber-300/[0.08] border border-amber-300/35 px-4 py-3 mb-1 flex items-center justify-between gap-2">
                                        <div>
                                            <div className="font-sans text-[10px] uppercase tracking-[0.28em] text-amber-200/85 mb-0.5">
                                                Keeper plan
                                            </div>
                                            <div className="font-serif italic text-amber-100 text-[13px] leading-snug">
                                                all 11 packs + motion + brand
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-serif text-amber-100 text-xl tracking-tight">$12.99</div>
                                            <div className="font-sans text-[9px] uppercase tracking-[0.22em] text-amber-200/70">/ mo</div>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            track("premium_pack_teaser_see_all", { pack_id: packId });
                                            onSeeAll();
                                        }}
                                        className="w-full py-3.5 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-medium text-sm tracking-wide transition-colors"
                                        data-testid="premium-pack-teaser-see-all"
                                    >
                                        unlock — sign in & pay
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            track("premium_pack_teaser_dismiss", { pack_id: packId });
                                            onClose();
                                        }}
                                        className="w-full py-2.5 rounded-full text-white/55 hover:text-white/85 text-sm transition-colors"
                                        data-testid="premium-pack-teaser-dismiss"
                                    >
                                        maybe later
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
